import { styled } from "@mui/material";
import CustomInput from "../../../CustomInput";
import PrimaryButton from "../../../../core/PrimaryButton";
import React, { ChangeEvent, FormEvent, Fragment, useCallback, useState } from "react";
import colors from "../../../../../theme/themeConstants";
import { apiAuth } from "../../../../../services";

interface IProps {
  onClose: () => void;
}

const emailRegExp = new RegExp(/^[A-Za-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}/);

const ResetPassword = ({ onClose }: IProps) => {
  const [email, setEmail] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>("");

  const handleChangeEmail = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    validateEmail(e.target.value);
  };

  const validateEmail = (value: string) => {
    let error = null;

    if (value.length === 0) {
      error = "Please specify your email address.";
      setEmailError(error);
      return;
    }

    if (!emailRegExp.test(value)) {
      error = "Invalid email";
      setEmailError(error);
      return;
    }

    setEmailError(error);
  };

  const isValid = useCallback(
    () => emailError === null,
    [emailError]
  );

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    validateEmail(email);

    if (!isValid()) {
      return;
    }

    setErrorMessage(null);
    setSuccessMessage(null);
    setIsSending(true);

    try {
      const { message } = await apiAuth.resetPassword({ email });
      setSuccessMessage(message);
    } catch (e: any) {
      const message = e?.response?.data?.error?.message || "Service unavailable";
      setErrorMessage(message);
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Container>
      <form onSubmit={handleSubmit}>
        <Block>
          <label>Email</label>
          <CustomInput
            value={email}
            onChange={handleChangeEmail}
            error={!!emailError}
            helperText={emailError ?? ""}
            fullWidth
          />
        </Block>

        { errorMessage &&
          <Block>
            <ErrorMessage>
              { errorMessage }
            </ErrorMessage>
          </Block>
        }

        { successMessage === null &&
          <PrimaryButton
            fullWidth
            type="submit"
            disabled={!isValid() || isSending}
          >
            Send
          </PrimaryButton>
        }

        { successMessage &&
          <Fragment>
            <Block>
              <SuccessMessage>
                { successMessage }
              </SuccessMessage>
            </Block>

            <PrimaryButton
              fullWidth
              type="button"
              onClick={handleClose}
            >
              Ok
            </PrimaryButton>
          </Fragment>
        }
      </form>
    </Container>
  );
};

export default ResetPassword;

const Container = styled("div")({
  marginTop: "2rem",
  display: "flex",
  flexDirection: "column",
  gap: "1rem"
});

const Block = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  marginBottom: "1rem",
  "& label": {
    fontSize: "12px",
    color: colors.textDark
  }
});

const ErrorMessage = styled("div")({
  color: "red",
  textAlign: "center",
  fontWeight: 400,
  fontSize: "0.75rem",
});

const SuccessMessage = styled("div")({
  textAlign: "center",
  fontWeight: 400,
  fontSize: "0.75rem",
});
